import { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Stack, Button, Text, Group, Box } from '@mantine/core';
import GardenBedButtonsMobile from '../components/GardenBedButtons.mobile';
import PreviewSVG from '../components/PreviewSVG';
import { colors, fontSize } from '../constants';

const { white } = colors;

const GardenBedEditorMobile = ({
	// State values
	shape,
	bedSize,
	
	// Handle functions
	handleNext,
	handleBack,
	handleSelectGardenShape,
	handleSelectGardenSize
}) => {
	// Ref for measuring container size
	const containerRef = useRef(null);

	// Step state management - 0: shape, 1: size
	const [currentStep, setCurrentStep] = useState(0);

	// Step navigation handlers
	const handleNextStep = () => {
		if (currentStep < 1) {
			setCurrentStep(currentStep + 1);
		}
	};

	const handlePreviousStep = () => {
		if (currentStep > 0) {
			setCurrentStep(currentStep - 1);
		}
	};

	// Enhanced handlers that include step navigation
	const handleShapeSelectionWithStep = (shapeId) => {
		handleSelectGardenShape(shapeId);
		// Auto-advance to next step if not on last step
		if (currentStep < 1) {
			setCurrentStep(currentStep + 1);
		}
	};

	const handleSizeSelectionWithStep = (sizeId) => {
		handleSelectGardenSize(sizeId);
		// Auto-advance to next step if not on last step
		if (currentStep < 1) {
			setCurrentStep(currentStep + 1);
		}
	};

	// Check if garden bed editor is complete
	const isGardenBedEditorDone = shape && bedSize;

	// Function to render the current step component
	const renderCurrentStep = () => {
		switch (currentStep) {
			case 0:
				return (
					<GardenBedButtonsMobile
						selectedShape={shape}
						selectedBedSize={bedSize}
						onShapeClick={handleSelectGardenShape}
						onSizeClick={handleSelectGardenSize}
						showShapes={true}
						showSizes={false}
					/>
				);
			case 1:
				return (
					<GardenBedButtonsMobile
						selectedShape={shape}
						selectedBedSize={bedSize}
						onShapeClick={handleSelectGardenShape}
						onSizeClick={handleSelectGardenSize}
						showShapes={false}
						showSizes={true}
					/>
				);
			default:
				return null;
		}
	};

	// Get step title
	const getStepTitle = () => {
		switch (currentStep) {
			case 0:
				return 'Choose Bed Shape';
			case 1:
				return 'Choose Bed Size';
			default:
				return 'Garden Bed';
		}
	};

	return (
		<Box style={{ 
			height: '100vh', 
			width: '100vw',
			display: 'flex', 
			flexDirection: 'column',
			backgroundColor: colors.mobileBackground,
			position: 'fixed',
			top: 0,
			left: 0,
			right: 0,
			bottom: 0,
			overflow: 'hidden',
			boxSizing: 'border-box'
		}}>

			{/* Header */}
			<Box style={{
				backgroundColor: colors.backgroundLight,
				height: '15vh',
				padding: '1vh',
				zIndex: 10,
				position: 'relative',
				borderBottom: `1.5vh solid ${colors.white}`,
				display: 'flex',
				flexDirection: 'column',
				justifyContent: 'flex-end',
				alignItems: 'center'
			}}>
				<Text style={{
					color: white,
					fontSize: fontSize.xxxl,
					fontWeight: 'bold',
					textAlign: 'center'
				}}>
					Garden Bed
				</Text>
			</Box>

			{/* Navigation Bar */}
			<Group justify="space-between" align="center" style={{ 
				padding: '2.5vh 3vw',
				zIndex: 10,
				position: 'relative',
				height: '10.5vh',
				flexShrink: 0
			}}>
				<Button
					variant="filled"
					onClick={handleBack}
					styles={{
						root: {
							backgroundColor: colors.buttonColor,
							color: colors.white,
							border: 'none',
							borderRadius: '1vh',
							padding: '1vh 5vw',
							fontWeight: 'bold',
							minHeight: '5.5vh',
							fontSize: fontSize.lg,
						}
					}}
				>
					Back
				</Button>
				
				<Button
					variant="filled"
					onClick={handleNext}
					disabled={!isGardenBedEditorDone}
					styles={{
						root: {
							backgroundColor: colors.buttonColor,
							color: colors.white,
							border: 'none',
							borderRadius: '1vh',
							padding: '1vh 5vw',
							fontWeight: 'bold',
							minHeight: '5.5vh',
							fontSize: fontSize.lg,
							opacity: isGardenBedEditorDone ? 1 : 0.5
						}
					}}
				>
					Finish
				</Button>
			</Group>

			{/* Main Content Area - PreviewSVG Background */}
			<Box 
				ref={containerRef}
				style={{ 
					flex: 1,
					minHeight: 0,
					position: 'relative',
					display: 'flex',
					flexDirection: 'column',
					overflow: 'hidden',
					paddingBottom: '25vh' // Space for the fixed bottom panel
				}}
			>
				{/* PreviewSVG fills the space as background */}
				<Box style={{
					flex: 1,
					minHeight: 0,
					position: 'relative',
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'center',
					overflow: 'hidden'
				}}>
					<PreviewSVG styleOverride={{ width: '104vw', marginLeft: '-1vw' }} />
				</Box>
			</Box>

			{/* Bottom Selection Panel - Fixed Position */}
			<Box style={{
				backgroundColor: colors.darkerGrey,
				borderRadius: '2.5vh',
				padding: '2.5vh',
				paddingTop: '2.5vh',
				paddingBottom: '1.25vh',
				paddingLeft: '1.25vh',
				paddingRight: '1.25vh',
				width: '90vw',
				height: '28vh',
				marginLeft: '5vw',
				position: 'fixed',
				bottom: '2.5vh',
				left: 0,
				right: 0,
				zIndex: 1000,
			}}>
				<Stack gap="0px" style={{ height: '100%' }}>
					{/* Current Step Component */}
					<Box style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', marginTop: '-2.5vh' }}>
						{renderCurrentStep()}
					</Box>
					
					{/* Navigation Buttons */}
					<Group justify="center" gap="0vw" style={{ marginTop: 'auto' }}>
						<Button
							variant="light"
							onClick={handlePreviousStep}
							styles={{
								root: {
									backgroundColor: colors.darkerGrey,
									color: colors.white,
									border: 'none',
									fontWeight: 'bold',
									minHeight: '5.5vh',
									fontSize: fontSize.xl,
									width: '35vw',
								}
							}}
						>
							{'< Previous'}
						</Button>
						
						<Button
							variant="filled"
							onClick={handleNextStep}
							disabled={currentStep === 1}
							styles={{
								root: {
									backgroundColor: colors.darkerGrey,
									color: colors.white,
									border: 'none',
									fontWeight: 'bold',
									minHeight: '5.5vh',
									fontSize: fontSize.xl,
									opacity: currentStep === 1 ? 0.5 : 1,
									width: '35vw',
								}
							}}
						>
							{'Next >'}
						</Button>
					</Group>
				</Stack>
			</Box>
		</Box>
	);
};

GardenBedEditorMobile.propTypes = {
	// State values
	shape: PropTypes.string,
	bedSize: PropTypes.number,
	
	// Handle functions
	handleNext: PropTypes.func.isRequired,
	handleBack: PropTypes.func.isRequired,
	handleSelectGardenShape: PropTypes.func.isRequired,
	handleSelectGardenSize: PropTypes.func.isRequired
};

export default GardenBedEditorMobile;
